import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { mockDataEspanol } from '../mockEspanol';
import { usePageTracking, trackFormSubmit, trackSignupStart } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Espanol = () => {
  const location = useLocation();
  
  // Track page view
  usePageTracking('/espanol');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });

  // Scroll to form if navigated from Start page
  useEffect(() => {
    if (location.state?.scrollToForm) {
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToPrivacy) {
      toast.error(mockDataEspanol.contactForm.privacyError);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contacts/`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        agreed_to_privacy: formData.agreedToPrivacy,
        source: 'espanol'
      });

      toast.success(mockDataEspanol.contactForm.successMessage);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || mockDataEspanol.contactForm.errorMessage;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookCall = () => {
    // Open Google Calendar link - use top window to avoid iframe restrictions
    if (window.top) {
      window.top.location.href = 'https://calendar.app.google/PLt1FQhRwFKXeyx16';
    } else {
      window.location.href = 'https://calendar.app.google/PLt1FQhRwFKXeyx16';
    }
  };

  const handleJoinNow = () => {
    setShowSignupModal(true);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.agreedToPrivacy) {
      toast.error('Debe aceptar la política de privacidad.');
      return;
    }

    if (!signupFormData.name || !signupFormData.email) {
      toast.error('Por favor complete su nombre y correo electrónico.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        name: signupFormData.name,
        email: signupFormData.email,
        phone: signupFormData.phone,
        agreed_to_privacy: signupFormData.agreedToPrivacy,
        source: 'espanol-signup'
      });

      const response = await axios.get(`${API}/join-links/next-rotation`);
      const joinLink = response.data.join_now_link;
      
      toast.success('¡Gracias! Será redirigido a la página de registro.');
      
      setShowSignupModal(false);
      setSignupFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });

      setTimeout(() => {
        window.open(joinLink, '_blank');
      }, 500);

    } catch (error) {
      console.error('Signup form error:', error);
      toast.error('Ocurrió un error. Por favor intente de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/epg_logo.png" alt="EPG Logo" className="logo-icon" />
            <span className="logo-text">{mockDataEspanol.header.logoText}</span>
          </div>
          <div className="header-buttons">
            <Button variant="outline" className="header-btn" onClick={handleBookCall}>
              Reserva una llamada
            </Button>
            <Button variant="outline" className="header-btn" onClick={() => window.open('/privacy-es', '_blank')}>
              Privacidad
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{mockDataEspanol.hero.badge}</div>
            <h1 className="hero-headline">{mockDataEspanol.hero.headline}</h1>
            <p className="hero-subheading">{mockDataEspanol.hero.subheading}</p>
            
            <div className="benefits-list">
              {mockDataEspanol.hero.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <Check className="check-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="stats-grid">
              {mockDataEspanol.hero.stats.map((stat, index) => (
                <Card key={index} className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                    {stat.sublabel && <div className="stat-sublabel">{stat.sublabel}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="hero-ctas">
              <Button className="cta-primary" onClick={handleBookCall}>
                {mockDataEspanol.hero.ctas[0].text}
              </Button>
              <Button className="cta-secondary" onClick={handleJoinNow}>
                {mockDataEspanol.hero.ctas[1].text}
              </Button>
            </div>

            <div className="hero-email">
              Email: <a href={`mailto:${mockDataEspanol.hero.email}`}>{mockDataEspanol.hero.email}</a>
            </div>
          </div>

          <div className="hero-video">
            <div 
              className="video-placeholder clickable-video" 
              onClick={() => window.open('https://www.7kmetals.com/alyssasimonsen/share/hpjio', '_blank')}
            >
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
              <div className="video-overlay">
                <div className="play-button">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="#000">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
                <span className="video-text">Haz clic para ver el video</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Videos Section */}
      <section className="main-videos-section">
        <div className="main-videos-container">
          <div className="video-item">
            <iframe 
              title="vimeo-player" 
              src="https://player.vimeo.com/video/1112783781?h=3c1cad70b7" 
              width="640" 
              height="360" 
              frameBorder="0" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
              allowFullScreen
              className="video-player-large"
            ></iframe>
            <p className="video-title-large">Estrategias de crecimiento de ganancias</p>
          </div>
          <div className="video-item">
            <iframe 
              title="vimeo-player-right" 
              src="https://player.vimeo.com/video/1055000943" 
              width="640" 
              height="360" 
              frameBorder="0" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
              allowFullScreen
              className="video-player-large"
            ></iframe>
            <p className="video-title-large">Acelerando el crecimiento de la riqueza</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          {mockDataEspanol.features.map((feature, index) => (
            <Card key={index} className="feature-card">
              <CardContent className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                {feature.title === "¡DESCUBRE MÁS!" && (
                  <Button 
                    onClick={handleBookCall}
                    style={{ 
                      marginTop: '1.5rem',
                      width: '100%',
                      background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                      color: '#000',
                      fontWeight: '600',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(212, 175, 55, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Reserva una llamada
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contact-form">
        <div className="contact-container">
          <h2 className="contact-title">{mockDataEspanol.contactForm.title}</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder="Nombre completo"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="email"
              name="email"
              placeholder="Correo electrónico"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="tel"
              name="phone"
              placeholder="Teléfono"
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : mockDataEspanol.contactForm.submitText}
            </Button>
            <div className="privacy-checkbox">
              <Checkbox
                id="privacy"
                checked={formData.agreedToPrivacy}
                onCheckedChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
              <label htmlFor="privacy" className="privacy-label">
                {mockDataEspanol.contactForm.privacyText}{' '}
                <a href="/privacy-es" target="_blank" rel="noopener noreferrer" className="privacy-link">aquí</a>.
              </label>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-cta">
            <span>¿Listo para comenzar tu viaje con EPG?</span>
            <Button className="footer-btn" onClick={handleBookCall}>
              Reserva una llamada
            </Button>
            <Button className="footer-btn-secondary" onClick={handleJoinNow}>
              ¡Únete ahora!
            </Button>
          </div>
          <div className="footer-info">
            <span>© 2024 Electrum Prosperity Group</span>
            <span>•</span>
            <a href={`mailto:${mockDataEspanol.hero.email}`}>{mockDataEspanol.hero.email}</a>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="signup-modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
            <button className="signup-modal-close" onClick={() => setShowSignupModal(false)}>
              <X size={24} />
            </button>
            <h2 className="signup-modal-title">Registro</h2>
            <p className="signup-modal-subtitle">Complete el formulario para continuar a la página de registro</p>
            
            <form onSubmit={handleSignupSubmit} className="signup-modal-form">
              <div className="signup-form-field">
                <Input
                  type="text"
                  id="signup_name_es"
                  placeholder="Nombre completo *"
                  value={signupFormData.name}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="email"
                  id="signup_email_es"
                  placeholder="Correo electrónico *"
                  value={signupFormData.email}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="tel"
                  id="signup_phone_es"
                  placeholder="Teléfono"
                  value={signupFormData.phone}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="signup-input"
                />
              </div>
              <div className="signup-checkbox-container">
                <Checkbox
                  id="signup-privacy-es"
                  checked={signupFormData.agreedToPrivacy}
                  onCheckedChange={(checked) => setSignupFormData(prev => ({ ...prev, agreedToPrivacy: checked }))}
                />
                <label htmlFor="signup-privacy-es" className="signup-checkbox-label">
                  Acepto la <a href="/privacy-es" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>
                </label>
              </div>
              <Button 
                type="submit" 
                className="signup-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Procesando...' : 'Siguiente'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Espanol;
